import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
  Label,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useChartConfig } from '../../charts/hooks/useChartConfig';
import { usePieActiveCell } from '../../charts/hooks/usePieActiveCell';

interface BatchStatsPieChartProps {
  scheduled: number;
  processing: number;
  sent: number;
  failed: number;
  total: number;
}

export const BatchStatsPieChart: React.FC<BatchStatsPieChartProps> = ({
  scheduled,
  processing,
  sent,
  failed,
  total,
}) => {
  const { t } = useTranslation();
  const config = useChartConfig();
  const { getCellProps } = usePieActiveCell();

  const data = [
    {
      name: t('broadcast.scheduled'),
      value: scheduled,
      color: config.colors.status.info,
    },
    {
      name: t('broadcast.processing'),
      value: processing,
      color: config.colors.status.warning,
    },
    {
      name: t('broadcast.sent'),
      value: sent,
      color: config.colors.status.success,
    },
    {
      name: t('broadcast.failed'),
      value: failed,
      color: config.colors.status.error,
    },
  ].filter((item) => item.value > 0);

  return (
    <div className="h-full w-full">
      <style>
        {`
          .recharts-wrapper:focus,
          .recharts-surface:focus {
            outline: none !important;
          }
          .recharts-layer:focus {
            outline: none !important;
          }
        `}
      </style>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart style={{ outline: 'none' }}>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={70}
            outerRadius={100}
            paddingAngle={2}
            dataKey="value"
            isAnimationActive={true}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                {...getCellProps(index)}
              />
            ))}
            <Label
              content={({ viewBox }) => {
                if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={(viewBox.cy || 0) - 15}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{ pointerEvents: 'none', userSelect: 'none' }}
                    >
                      <tspan
                        x={viewBox.cx}
                        dy="-0.6em"
                        className="fill-gray-900 text-2xl font-bold"
                      >
                        {total}
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        dy="2.2em"
                        className="fill-gray-500 text-sm"
                      >
                        {t('broadcast.totalBatches')}
                      </tspan>
                    </text>
                  );
                }
              }}
            />
          </Pie>
          <Tooltip {...config.tooltip} />
          <Legend
            {...config.legend}
            iconType="circle"
            layout="horizontal"
            align="center"
            verticalAlign="bottom"
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};
